import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { Check, CircleX, LoaderCircle, Rocket } from 'lucide-react';
import type { ScheduleVersionRow } from '../../data/database.types';
import { qk, useChangesSinceDraft, useOrg, usePilotDirectory, useRun, useSettings } from '../../data/queries';
import { useMember } from '../../app/AuthProvider';
import { useToast } from '../../ui/toast';
import { Button, Chip, ErrorNote, Spinner } from '../../ui/components';
import { Modal } from '../../ui/dialog';
import { DateInput, Field, TextInput } from '../../ui/fields';
import { addDaysIso, fmtDate, fmtDateTime, todayIso } from '../../ui/format';
import { severityCounts } from '../schedule/FindingsList';
import { checkPublish, publishDraft, type PublishCheck, type PublishStep } from './publishDraft';

const STEPS: { id: PublishStep; label: string }[] = [
  { id: 'snapshot', label: 'Read the inputs' },
  { id: 'hash', label: 'Inputs unchanged since the draft' },
  { id: 'rerun', label: 'Re-run the engine' },
  { id: 'compare', label: 'Result matches the reviewed draft' },
  { id: 'diff', label: 'Differences against the published version' },
];
const ORDER: PublishStep[] = ['snapshot', 'hash', 'rerun', 'compare', 'diff', 'done'];

/**
 * Publish (spec 6.2). The button is enabled only when a current draft
 * exists and no input changed since; the modal re-checks everything with a
 * fresh snapshot and an engine re-run (D13) before offering to publish.
 */
export function PublishButton({ variant = 'secondary' }: { variant?: 'primary' | 'secondary' }) {
  const { org: orgInitial } = useMember();
  const org = useOrg(orgInitial.id, orgInitial);
  const run = useRun(org.data?.current_draft_run_id ?? null);
  const edits = useChangesSinceDraft(orgInitial.id, run.data?.created_at ?? null);
  const [open, setOpen] = useState(false);
  const enabled = !!run.data && run.data.ok && (edits.data ?? 0) === 0;
  const title = !run.data ? 'Generate a draft first' : !run.data.ok ? 'The draft did not generate successfully' : (edits.data ?? 0) > 0 ? 'Inputs changed since the draft. Regenerate first.' : 'Publish the reviewed draft as the schedule of record';
  return (
    <>
      <Button variant={variant} disabled={!enabled} title={title} onClick={() => setOpen(true)}>
        <Rocket size={16} /> Publish
      </Button>
      {open && <PublishDialog open={open} onOpenChange={setOpen} orgId={orgInitial.id} currentVersionId={org.data?.current_version_id ?? null} />}
    </>
  );
}

function PublishDialog({ open, onOpenChange, orgId, currentVersionId }: { open: boolean; onOpenChange(o: boolean): void; orgId: string; currentVersionId: string | null }) {
  const org = useOrg(orgId);
  const run = useRun(org.data?.current_draft_run_id ?? null);
  const directory = usePilotDirectory(orgId);
  const settings = useSettings(orgId);
  const qc = useQueryClient();
  const toast = useToast();
  const [step, setStep] = useState<PublishStep | null>(null);
  const [check, setCheck] = useState<PublishCheck | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [label, setLabel] = useState('');
  const [ack, setAck] = useState(false);
  const [releaseDate, setReleaseDate] = useState<string | null>(null);
  const [wholeWindow, setWholeWindow] = useState(false);
  const [releaseInit, setReleaseInit] = useState(false);
  const [publishing, setPublishing] = useState(false);
  const [published, setPublished] = useState<ScheduleVersionRow | null>(null);
  const names = useMemo(() => new Map((directory.data ?? []).filter((p) => p.code).map((p) => [p.code!, `${p.last_name ?? ''}, ${p.first_name ?? ''}`])), [directory.data]);

  useEffect(() => {
    if (!open || run.isLoading) return;
    let cancelled = false;
    setCheck(null);
    setError(null);
    setStep('snapshot');
    checkPublish(orgId, run.data ?? null, currentVersionId, (s) => !cancelled && setStep(s))
      .then((c) => {
        if (cancelled) return;
        setCheck(c);
        // Default release date (D77): keep the previous version's if still ahead, else today + the settings lead.
        if (c.kind === 'ready' && !releaseInit) {
          const prev = c.publishedVersion?.released_through ?? null;
          const lead = settings.data?.release_lead_weeks ?? 6;
          const suggested = prev && prev >= todayIso() ? prev : addDaysIso(todayIso(), lead * 7);
          const prevWhole = !!c.publishedVersion && c.publishedVersion.released_through === null;
          setWholeWindow(prevWhole);
          setReleaseDate(suggested);
          setReleaseInit(true);
        }
      })
      .catch((e) => !cancelled && setError(e instanceof Error ? e.message : String(e)))
      .finally(() => !cancelled && setStep(null));
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, run.data?.id, run.isLoading]);

  const doPublish = async () => {
    if (!run.data) return;
    setPublishing(true);
    setError(null);
    try {
      const v = await publishDraft(run.data.id, label, wholeWindow ? null : releaseDate);
      setPublished(v);
      await Promise.all([
        qc.invalidateQueries({ queryKey: qk.org(orgId) }),
        qc.invalidateQueries({ queryKey: ['schedule_versions'] }),
        qc.invalidateQueries({ queryKey: ['schedule_version'] }),
        qc.invalidateQueries({ queryKey: ['schedule_assignments'] }),
        qc.invalidateQueries({ queryKey: ['carry_state'] }),
        qc.invalidateQueries({ queryKey: ['amendment_log'] }),
        qc.invalidateQueries({ queryKey: ['live_checks'] }),
      ]);
      toast.show({ message: `Published v${v.version_no}`, tone: 'ok' });
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setPublishing(false);
    }
  };

  const stepIndex = step ? ORDER.indexOf(step) : check ? ORDER.length : -1;
  const failedAt: PublishStep | null = check?.kind === 'stale' ? 'hash' : check?.kind === 'invalid' ? 'rerun' : check?.kind === 'mismatch' ? 'compare' : null;
  const canPublish = check?.kind === 'ready' && !publishing && !published && (check.gaps === 0 || ack) && (wholeWindow || !!releaseDate);

  return (
    <Modal
      open={open}
      onOpenChange={(o) => !publishing && onOpenChange(o)}
      title="Publish"
      description="Re-runs the engine; it must reproduce the reviewed draft exactly."
      width="lg"
      footer={
        <>
          {published ? (
            <>
              <Link to={`/scheduler/schedule?source=published&version=${published.id}`} className="inline-flex items-center rounded-md bg-navy px-3 py-1.5 text-sm font-medium text-white hover:bg-navy-deep" onClick={() => onOpenChange(false)}>
                Open v{published.version_no}
              </Link>
              <Link to="/scheduler/published" className="inline-flex items-center rounded-md border border-line px-3 py-1.5 text-sm hover:bg-band" onClick={() => onOpenChange(false)}>
                Published
              </Link>
            </>
          ) : (
            <Button variant="primary" disabled={!canPublish} busy={publishing} onClick={() => void doPublish()}>
              <Rocket size={14} /> Publish{check?.kind === 'ready' ? ` as v${(check.publishedVersion?.version_no ?? 0) + 1}` : ''}
            </Button>
          )}
          <Button onClick={() => onOpenChange(false)} disabled={publishing}>Close</Button>
        </>
      }
    >
      <div className="space-y-4 text-sm">
        {!published && (
          <ol className="space-y-1.5">
            {STEPS.map((s, i) => {
              const state = failedAt === s.id ? 'error' : i < stepIndex ? 'done' : i === stepIndex && step ? 'active' : 'todo';
              return (
                <li key={s.id} className="flex items-center gap-2">
                  {state === 'done' && <Check size={16} className="text-ok" />}
                  {state === 'active' && <LoaderCircle size={16} className="animate-spin text-navy" />}
                  {state === 'error' && <CircleX size={16} className="text-danger" />}
                  {state === 'todo' && <span className="inline-block h-4 w-4 rounded-full border border-line" />}
                  <span className={state === 'todo' ? 'text-ink-muted' : ''}>{s.label}</span>
                </li>
              );
            })}
          </ol>
        )}
        {error && <ErrorNote message={error} />}
        {run.isLoading && <Spinner />}

        {check?.kind === 'no-draft' && <ErrorNote message="There is no draft to publish. Generate one first." />}
        {check?.kind === 'not-ok' && <ErrorNote message={'The draft did not generate successfully: ' + check.errors.slice(0, 3).join(' ')} />}
        {check?.kind === 'stale' && <ErrorNote message="Inputs changed since the draft. Regenerate first, review the new draft, then publish." />}
        {check?.kind === 'invalid' && <ErrorNote message={'The inputs no longer validate: ' + check.errors.slice(0, 3).join(' ')} />}
        {check?.kind === 'mismatch' && (
          <div className="space-y-1">
            <ErrorNote message={`Regenerating now would produce a different schedule (${check.mismatches.length} rotation${check.mismatches.length === 1 ? '' : 's'} differ). Generate Draft again, review it, then publish.`} />
            {check.engineVersion !== check.draftEngineVersion && (
              <p className="text-xs text-ink-muted">The draft was generated with engine {check.draftEngineVersion}; this build runs {check.engineVersion}.</p>
            )}
          </div>
        )}

        {check?.kind === 'ready' && !published && (
          <>
            <ReadySummary check={check} names={names} label={label} setLabel={setLabel} ack={ack} setAck={setAck} />
            <div className="rounded-md border border-line px-3 py-2">
              <h3 className="mb-1 text-xs font-semibold uppercase tracking-wide text-navy">Release to pilots</h3>
              <p className="mb-2 text-xs text-ink-muted">Pilots see rotations starting on or before this date. Extend it later from the Published page.</p>
              <div className="flex flex-wrap items-end gap-3">
                <Field label="Released through">
                  <DateInput value={wholeWindow ? null : releaseDate} onChange={(v) => { setReleaseDate(v); if (v) setWholeWindow(false); }} disabled={wholeWindow} />
                </Field>
                <label className="flex items-center gap-2 pb-2 text-sm">
                  <input type="checkbox" checked={wholeWindow} onChange={(e) => setWholeWindow(e.target.checked)} /> Release the whole window
                </label>
              </div>
            </div>
          </>
        )}

        {published && (
          <div className="space-y-2">
            <p className="font-medium text-ok">Published v{published.version_no}{published.label ? ` "${published.label}"` : ''} at {fmtDateTime(published.published_at)}.</p>
            <dl className="grid grid-cols-[10rem_1fr] gap-y-1">
              <dt className="text-ink-muted">Window</dt>
              <dd>{fmtDate(published.report_start)} to {fmtDate(published.horizon_end)}</dd>
              <dt className="text-ink-muted">Rotations</dt>
              <dd>{published.rotation_count}</dd>
              <dt className="text-ink-muted">Unfilled seats</dt>
              <dd>{published.gap_count}</dd>
              <dt className="text-ink-muted">Released to pilots</dt>
              <dd>{published.released_through ? `through ${fmtDate(published.released_through)}` : 'whole window'}</dd>
            </dl>
          </div>
        )}
      </div>
    </Modal>
  );
}

function ReadySummary({ check, names, label, setLabel, ack, setAck }: { check: Extract<PublishCheck, { kind: 'ready' }>; names: Map<string, string>; label: string; setLabel(v: string): void; ack: boolean; setAck(v: boolean): void }) {
  const counts = severityCounts(check.violations);
  const changes = Array.from(check.diff.values()).sort((a, b) => a.rotationStart.localeCompare(b.rotationStart) || a.tail.localeCompare(b.tail) || a.seat.localeCompare(b.seat));
  const nm = (code: string | null) => (code ? names.get(code) ?? code : 'OPEN');
  return (
    <div className="space-y-3">
      <Field label="Label (optional)" hint="e.g. September window">
        <TextInput value={label} onChange={(e) => setLabel(e.target.value)} maxLength={80} autoFocus />
      </Field>
      <dl className="grid grid-cols-[10rem_1fr] gap-y-1">
        <dt className="text-ink-muted">Rotations</dt>
        <dd>{check.draftRows.length}</dd>
        <dt className="text-ink-muted">Unfilled seats</dt>
        <dd className={check.gaps ? 'font-semibold text-danger' : ''}>{check.gaps}</dd>
        <dt className="text-ink-muted">Findings</dt>
        <dd className="flex flex-wrap gap-1">
          {counts.HIGH > 0 && <Chip tone="danger">{counts.HIGH} HIGH</Chip>}
          {counts.WARN > 0 && <Chip tone="warn">{counts.WARN} WARN</Chip>}
          {counts.SOFT > 0 && <Chip tone="gold">{counts.SOFT} SOFT</Chip>}
          {counts.INFO > 0 && <Chip tone="neutral">{counts.INFO} INFO</Chip>}
          {check.violations.length === 0 && <span>none</span>}
        </dd>
        <dt className="text-ink-muted">Engine</dt>
        <dd className="font-mono text-xs">{check.engineVersion}</dd>
      </dl>

      <div>
        <h3 className="mb-1 text-xs font-semibold uppercase tracking-wide text-navy">
          {check.publishedVersion ? `Changes against v${check.publishedVersion.version_no}` : 'First publish'}
        </h3>
        {!check.publishedVersion && <p className="text-ink-muted">Every rotation is new.</p>}
        {check.publishedVersion && changes.length === 0 && <p className="text-ok">Identical to v{check.publishedVersion.version_no} seat for seat. Publishing records a new version with no amendments.</p>}
        {changes.length > 0 && (
          <div className="max-h-56 overflow-y-auto rounded-md border border-line">
            <table className="w-full text-xs">
              <thead className="sticky top-0 bg-band text-left text-ink-muted">
                <tr><th className="px-2 py-1">Tail</th><th className="px-2 py-1">Rotation</th><th className="px-2 py-1">Seat</th><th className="px-2 py-1">Before</th><th className="px-2 py-1">After</th></tr>
              </thead>
              <tbody>
                {changes.map((c) => (
                  <tr key={`${c.tail}|${c.rotationStart}|${c.seat}`} className="border-t border-line">
                    <td className="px-2 py-1 font-mono">{c.tail}</td>
                    <td className="whitespace-nowrap px-2 py-1">{fmtDate(c.rotationStart, { year: false })}</td>
                    <td className="px-2 py-1">{c.seat}</td>
                    <td className="px-2 py-1">{c.kind === 'added' ? <span className="text-ink-muted">(new rotation)</span> : nm(c.before)}</td>
                    <td className="px-2 py-1">{c.kind === 'removed' ? <span className="text-ink-muted">(removed)</span> : nm(c.after)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        {changes.length > 0 && <p className="mt-1 text-xs text-ink-muted">{changes.length} seat{changes.length === 1 ? '' : 's'} change.</p>}
      </div>

      {check.gaps > 0 && (
        <label className="flex items-start gap-2 rounded-md border border-danger-soft bg-danger-soft px-3 py-2 text-danger">
          <input type="checkbox" className="mt-1" checked={ack} onChange={(e) => setAck(e.target.checked)} />
          <span>I understand {check.gaps} seat{check.gaps === 1 ? ' is' : 's are'} unfilled in this schedule. The findings list names who could cover each one with a double.</span>
        </label>
      )}
    </div>
  );
}
